

























































import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import {
    Options,
    Option,
    CardIndex
} from '../../../entities/conversation'
import OptionEditor from './OptionEditor.vue'
import { sleep } from '../../../utils/async'
import vClickOutside from 'v-click-outside'

type FocusInput = 'key' | 'value'

@Component<OptionsEditor>({
    components: {
        OptionEditor
    },
    directives: {
        clickOutside: vClickOutside.directive
    },
    model: {
        prop: 'options',
        event: 'change'
    }
})
export default class OptionsEditor extends Vue {

    @Prop({ type: Array, default: [] }) public options!: Options
    @Prop(Boolean) public fixed?: boolean // if true, can't add or remove options, or edit their keys
    @Prop(Boolean) public disableFlowJumpEditor?: boolean
    @Prop(Object) public cardIndex!: CardIndex
    @Prop(Number) public max?: number
    @Prop(Boolean) public canEditKeys!: boolean
    @Prop(Boolean) public linkToScorecard!: boolean
    @Prop({type: String, default: ''}) public scorecardTab!: ''
    @Prop({ type: Array, default: [] }) public errorMessages!: string[]
    @Prop({ type: Array, default: [] }) public errorIndices!: number[]

    public focusedIndex = -1
    public focusedInput: FocusInput = 'value' // In the UI, 'value' is called 'label' and 'key' is called 'value'
    public message = ''
    public hasBeenWarned = false

    public get showHitEnter() {
        return this.focusedIndex > -1 &&
            this.focusedIndex === this.options.length - 1 &&
            (this.canEditKeys ? this.focusedInput === 'key' : true)
    }

    public optionIsInvalid(index: number) {
        return this.errorIndices.indexOf(index) > -1
    }

    public updateOptions(options: Options) {
        this.$emit('change', options)
    }

    public updateOption(index: number, option: Option) {
        const updated = this.options.map((opt, idx) =>
            idx === index ?
            option :
            opt
        )
        this.updateOptions(updated)
    }

    public addOption() {
        if (this.options.length === this.max) {
            return
        }
        const key = `option${this.options.length + 1}`
        this.updateOptions(this.options.concat([
            { key, value: key }
        ]))
        this.focusedIndex = this.options.length
    }

    public removeOption(index: number) {
        this.updateOptions(
            this.options.filter((opt, idx) => idx !== index)
        )
    }

    public handleEnterKey(index: number, sourceInput: FocusInput) {

        const atEnd = this.focusedIndex > -1 && this.options.length - 1 === this.focusedIndex

        if (this.canEditKeys && sourceInput === 'value') {
            this.focusedInput = 'key'

        } else {
            this.focusedInput = 'value'
            if (atEnd) {
                this.addOption()
            } else {
                this.focusedIndex = index + 1
            }
        }
    }

    public handleFocus(index: number, sourceInput: FocusInput) {
        this.focusedInput = sourceInput
        this.focusedIndex = index
    }

    public resetFocus() {
        this.focusedInput = 'value'
        this.focusedIndex = -1
    }

    public get hasClipboard() {
        return !!(navigator as any).clipboard
    }

    public async copyToClipboard() {
        const clipboard = (navigator as any).clipboard
        await clipboard.writeText(
            JSON.stringify(this.options)
        )
        this.flash('Copied Successfully')
    }

    public async pasteFromClipboard() {

        const text = await (navigator as any).clipboard.readText()

        try {
            const parsed = JSON.parse(text)
            if (parsed) {
                const options = parsed as Option[]

                this.updateOptions(
                    this.linkToScorecard ?
                    options :
                    options.map(({ key, value }) => ({ key, value }))
                    // strip badgeIds if not valid in current context
                )
                this.flash('Pasted Successfully')
            }
        } catch (err) {
            this.flash('Sorry, this is not a valid options list', 2500)
        }
    }

    public async flash(message: string, waitMilliSeconds = 1000) {
        this.message = message
        await sleep(waitMilliSeconds)
        this.message = ''
    }

    public notifyWarning() {
        if (!this.hasBeenWarned) {
            alert('Please note that when changing values it may take a few minutes to update all your responses.')
            this.hasBeenWarned = true
        }
    }
}
